/**
 * Sidebar palette for inserting predefined HTML blocks into TinyMCE (click or drag).
 * Compact width + wireframe previews maximize editor space on large screens.
 */

import { useId } from "react";
import { Plus } from "lucide-react";

import {
	CMS_BLOCK_DRAG_MIME,
	type CMSEditorBlock,
} from "@/lib/cms-editor-blocks";

interface CMSComponentLibrarySidebarProps {
	blocks: readonly CMSEditorBlock[];
	onInsert: (html: string) => void;
}

/** Minimal layout sketch per block type (visual cue at small size). */
function BlockWireframe({
	blockId,
	gradientId,
}: Readonly<{ blockId: string; gradientId: string }>): JSX.Element {
	const stroke = "#94a3b8";
	const fill = "#f8fafc";
	const accent = "#3b82f6";

	switch (blockId) {
		case "blank-section":
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<rect x="2" y="2" width="44" height="24" rx="2" fill="#ffffff" stroke={stroke} strokeWidth="1" strokeDasharray="2 2" />
					<line x1="8" y1="11" x2="32" y2="11" stroke={accent} strokeWidth="1.3" strokeLinecap="round" />
					<line x1="8" y1="16" x2="40" y2="16" stroke={stroke} strokeWidth="1.1" strokeLinecap="round" />
				</svg>
			);
		case "three-column-cards":
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<rect x="1" y="3" width="14" height="22" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1" />
					<rect x="17" y="3" width="14" height="22" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1" />
					<rect x="33" y="3" width="14" height="22" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1" />
				</svg>
			);
		case "two-column-split":
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<rect x="1" y="3" width="21" height="22" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1" />
					<rect x="26" y="3" width="21" height="22" rx="1.5" fill="#fff" stroke={accent} strokeWidth="1.2" />
				</svg>
			);
		case "cta-band":
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<defs>
						<linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
							<stop offset="0%" stopColor="#1A5276" />
							<stop offset="100%" stopColor="#148F77" />
						</linearGradient>
					</defs>
					<rect x="1" y="6" width="46" height="16" rx="3" fill={`url(#${gradientId})`} />
					<rect x="15" y="11" width="18" height="6" rx="2" fill="#fff" opacity="0.35" />
				</svg>
			);
		case "quote-block":
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<rect x="4" y="4" width="42" height="20" rx="2" fill="#f1f5f9" stroke={stroke} strokeWidth="1" />
					<rect x="4" y="4" width="3.5" height="20" rx="1" fill="#1A5276" />
					<line x1="12" y1="10" x2="38" y2="10" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" />
					<line x1="12" y1="15" x2="32" y2="15" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
				</svg>
			);
		default:
			return (
				<svg viewBox="0 0 48 28" className="h-7 w-full max-w-[4.5rem]" aria-hidden>
					<rect x="4" y="5" width="40" height="18" rx="2" fill={fill} stroke={stroke} strokeWidth="1" />
				</svg>
			);
	}
}

export function CMSComponentLibrarySidebar({
	blocks,
	onInsert,
}: Readonly<CMSComponentLibrarySidebarProps>): JSX.Element {
	const instancePrefix = useId().replace(/:/g, "");

	return (
		<aside
			className="hidden lg:flex w-28 xl:w-32 shrink-0 flex-col border-l border-gray-200 bg-gray-50"
			aria-label="Insert layout blocks"
		>
			<div className="shrink-0 px-2 py-1.5 border-b border-gray-200 bg-white">
				<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 leading-none">
					Blocks
				</p>
				<p
					className="text-[9px] text-gray-400 mt-1 leading-tight"
					title="Drag a block onto the editor, or use Insert."
				>
					Drag or +
				</p>
			</div>
			<div className="flex-1 min-h-0 overflow-y-auto p-1.5 space-y-1.5">
				{blocks.map((block) => (
					<article
						key={block.id}
						draggable
						onDragStart={(e) => {
							e.dataTransfer.setData(CMS_BLOCK_DRAG_MIME, block.id);
							e.dataTransfer.setData("text/html", block.html);
							e.dataTransfer.setData("text/plain", block.html);
							e.dataTransfer.effectAllowed = "copy";
						}}
						title={`${block.title} — ${block.description}`}
						aria-label={`${block.title}. Drag into editor or use Insert.`}
						className="group rounded-md border border-gray-200 bg-white p-1 shadow-sm cursor-grab active:cursor-grabbing select-none hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
					>
						<div className="flex flex-col items-center gap-0.5">
							<div className="w-full rounded bg-gray-50/80 px-1 py-0.5 flex justify-center border border-gray-100">
								<BlockWireframe
									blockId={block.id}
									gradientId={`${instancePrefix}-cta-${block.id}`}
								/>
							</div>
							<p className="text-[9px] font-semibold text-gray-800 text-center leading-tight line-clamp-2 px-0.5 min-h-[2.5rem]">
								{block.title}
							</p>
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onInsert(block.html);
								}}
								className="flex h-5 w-full items-center justify-center gap-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
								aria-label={`Insert ${block.title}`}
							>
								<Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
							</button>
						</div>
					</article>
				))}
			</div>
		</aside>
	);
}
